/**
 * Cached Firestore Service
 * Wraps Firestore operations with IndexedDB caching
 * Updated: All exports now available
 */

import {
  collection as firestoreCollection,
  doc as firestoreDoc,
  getDoc as firestoreGetDoc,
  getDocs as firestoreGetDocs,
  setDoc as firestoreSetDoc,
  updateDoc as firestoreUpdateDoc,
  deleteDoc as firestoreDeleteDoc,
  addDoc as firestoreAddDoc,
  query as firestoreQuery,
  getFirestore,
  Query,
  QueryConstraint,
  DocumentReference,
  CollectionReference,
  DocumentData,
  DocumentSnapshot,
  QuerySnapshot,
  QueryDocumentSnapshot,
  Firestore,
  Timestamp,
  where,
  orderBy,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  startAt,
  endAt,
  getCountFromServer,
  serverTimestamp,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';

import { firestoreCache, CacheOptions, DEFAULT_TTL } from './firestoreCache';

/**
 * Serialize query constraints for cache key generation
 */
function serializeQueryConstraints(constraints: QueryConstraint[]): string {
  return constraints.map(constraint => {
    const serialized = constraint.toString();
    return serialized;
  }).join('|');
}

/**
 * Convert Firestore data to a serializable format
 */
function serializeFirestoreData(data: any): any {
  if (!data) return data;
  
  // Handle Firestore Timestamp
  if (data.toDate && typeof data.toDate === 'function') {
    return { _timestamp: data.toDate().toISOString() };
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(serializeFirestoreData);
  }
  
  // Handle objects
  if (typeof data === 'object') {
    const serialized: any = {};
    for (const key in data) {
      serialized[key] = serializeFirestoreData(data[key]);
    }
    return serialized;
  }
  
  return data;
}

/**
 * Deserialize cached data back to original format
 */
function deserializeFirestoreData(data: any): any {
  if (!data) return data;
  
  // Handle serialized timestamp
  if (data._timestamp) {
    return new Date(data._timestamp);
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(deserializeFirestoreData);
  }
  
  // Handle objects
  if (typeof data === 'object') {
    const deserialized: any = {};
    for (const key in data) {
      deserialized[key] = deserializeFirestoreData(data[key]);
    }
    return deserialized;
  }
  
  return data;
}

/**
 * Get a single document with caching
 */
export async function getDoc<T = DocumentData>(
  docRef: DocumentReference<T>,
  options?: CacheOptions
): Promise<DocumentSnapshot<T>> {
  const ttl = options?.ttl ?? DEFAULT_TTL;
  const forceRefresh = options?.forceRefresh ?? false;
  
  // Generate cache key from document path
  const cacheKey = `doc:${docRef.path}`;
  
  // Try to get from cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = await firestoreCache.get<any>(cacheKey);
    if (cached) {
      // Return a mock DocumentSnapshot
      const deserializedData = deserializeFirestoreData(cached.data);
      return {
        exists: () => cached.exists,
        data: () => deserializedData,
        id: cached.id,
        ref: docRef,
        metadata: cached.metadata,
      } as DocumentSnapshot<T>;
    }
  }
  
  // Fetch from Firestore
  const snapshot = await firestoreGetDoc(docRef);
  
  // Cache the result
  const cacheData = {
    exists: snapshot.exists(),
    data: snapshot.exists() ? serializeFirestoreData(snapshot.data()) : null,
    id: snapshot.id,
    metadata: snapshot.metadata,
  };
  
  await firestoreCache.set(cacheKey, cacheData, ttl);
  
  return snapshot;
}

/**
 * Get multiple documents with caching
 */
export async function getDocs<T = DocumentData>(
  query: Query<T>,
  options?: CacheOptions
): Promise<QuerySnapshot<T>> {
  const ttl = options?.ttl ?? DEFAULT_TTL;
  const forceRefresh = options?.forceRefresh ?? false;
  
  // Generate cache key from query
  // Note: This is a simplified approach. For production, you'd want a more sophisticated key generation
  const queryStr = query.toString();
  const cacheKey = `query:${queryStr}`;
  
  console.log(`🔍 Cache key: ${cacheKey.substring(0, 100)}...`);
  
  // Try to get from cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = await firestoreCache.get<any>(cacheKey);
    if (cached) {
      console.log(`🎯 Cache HIT for query (${cached.size} docs)`);
      // Return a mock QuerySnapshot
      const deserializedDocs = cached.docs.map((doc: any) => ({
        exists: () => true,
        data: () => deserializeFirestoreData(doc.data),
        id: doc.id,
        ref: doc.ref,
      }));
      
      return {
        docs: deserializedDocs,
        empty: cached.empty,
        size: cached.size,
        forEach: (callback: any) => deserializedDocs.forEach(callback),
        metadata: cached.metadata,
      } as QuerySnapshot<T>;
    } else {
      console.log(`❌ Cache MISS for query`);
    }
  } else {
    console.log(`🔄 Force refresh - skipping cache`);
  }
  
  // Fetch from Firestore
  console.log(`🌐 Fetching from Firestore...`);
  const snapshot = await firestoreGetDocs(query);
  console.log(`✅ Firestore returned ${snapshot.size} docs`);
  
  // Cache the result
  const cacheData = {
    docs: snapshot.docs.map(doc => ({
      id: doc.id,
      data: serializeFirestoreData(doc.data()),
      ref: doc.ref.path,
    })),
    empty: snapshot.empty,
    size: snapshot.size,
    metadata: snapshot.metadata,
  };
  
  await firestoreCache.set(cacheKey, cacheData, ttl);
  console.log(`💾 Cached ${snapshot.size} docs with TTL ${ttl}ms`);
  
  return snapshot;
}

/**
 * Create a query with the same API as Firestore
 */
export function query<T = DocumentData>(
  collectionRef: Query<T> | CollectionReference<T>,
  ...queryConstraints: QueryConstraint[]
): Query<T> {
  return firestoreQuery(collectionRef, ...queryConstraints);
}

/**
 * Invalidate cache for a specific document or collection
 */
export async function invalidateCache(path: string): Promise<void> {
  // Invalidate specific document
  await firestoreCache.delete(`doc:${path}`);
  
  // Note: Invalidating related queries is complex and would require
  // tracking which queries include this document
  console.log(`Cache invalidated for: ${path}`);
}

/**
 * Write operations that invalidate cache
 */

export async function setDoc<T = DocumentData>(
  docRef: DocumentReference<T>,
  data: T,
  options?: any
): Promise<void> {
  // Perform the write
  await firestoreSetDoc(docRef, data, options);
  
  // Invalidate cache for this document
  await invalidateCache(docRef.path);
  
  // Invalidate collection cache (simplified - invalidates all queries)
  const collectionPath = docRef.parent.path;
  await firestoreCache.delete(`query:${collectionPath}`);
}

export async function updateDoc<T = DocumentData>(
  docRef: DocumentReference<T>,
  data: any
): Promise<void> {
  // Perform the update
  await firestoreUpdateDoc(docRef, data);
  
  // Invalidate cache for this document
  await invalidateCache(docRef.path);
  
  // Invalidate collection cache
  const collectionPath = docRef.parent.path;
  await firestoreCache.delete(`query:${collectionPath}`);
}

export async function deleteDoc<T = DocumentData>(
  docRef: DocumentReference<T>
): Promise<void> {
  // Perform the delete
  await firestoreDeleteDoc(docRef);
  
  // Invalidate cache for this document
  await invalidateCache(docRef.path);
  
  // Invalidate collection cache
  const collectionPath = docRef.parent.path;
  await firestoreCache.delete(`query:${collectionPath}`);
}

export async function addDoc<T = DocumentData>(
  collectionRef: CollectionReference<T>,
  data: T
): Promise<DocumentReference<T>> {
  // Perform the add
  const docRef = await firestoreAddDoc(collectionRef, data);
  
  // Invalidate collection cache
  await firestoreCache.delete(`query:${collectionRef.path}`);
  
  return docRef;
}

/**
 * Re-export other Firestore functions that don't need caching
 */
export { 
  firestoreCollection as collection,
  firestoreDoc as doc,
  // Firestore utilities
  getFirestore,
  Timestamp,
  QueryDocumentSnapshot,
  getCountFromServer,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  // Query constraints
  where,
  orderBy,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  startAt,
  endAt,
};

// Export types
export type { DocumentData, Query, CollectionReference, DocumentReference, QuerySnapshot, DocumentSnapshot };

/**
 * Export cache management functions
 */
export const cacheManager = {
  clear: () => firestoreCache.clear(),
  cleanupExpired: () => firestoreCache.cleanupExpired(),
  getStats: () => firestoreCache.getStats(),
  invalidate: invalidateCache,
};

// Export types
export type { CacheOptions };
export { DEFAULT_TTL };
